import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Button, Input, Modal, Form, Select, 
  Card, Row, Col, Space, Tag, Statistic,
  Typography, Avatar, Tooltip, Layout, theme,
  Radio, Alert
} from 'antd';
import { 
  FaRegTrashAlt, FaEdit, FaPhoneAlt, FaUser,
  FaMapMarkerAlt, FaNotesMedical, FaCalendarAlt,
  FaPlus, FaSearch, FaFilter, FaSync,
  FaFileMedical, FaQuestionCircle, FaIdCard,
  FaExclamationTriangle
} from 'react-icons/fa';
import { 
  PlusOutlined, SearchOutlined, FilterOutlined,
  PhoneOutlined, UserOutlined, MedicineBoxOutlined,
  EnvironmentOutlined, CalendarOutlined, FileTextOutlined,
  SyncOutlined, WhatsAppOutlined, WarningOutlined
} from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Highlighter from 'react-highlight-words';
import api from '../api/Api';

const { Title, Text } = Typography;
const { Option } = Select;
const { useToken } = theme;
const { Content } = Layout;

const Chamada = () => {
  const { token } = useToken();
  
  const [chamadas, setChamadas] = useState([]);
  const [patologias, setPatologias] = useState([]);
  const [questionarios, setQuestionarios] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedChamada, setSelectedChamada] = useState(null);
  const [chamadaToDelete, setChamadaToDelete] = useState(null);
  const [respostaQuestionario, setRespostaQuestionario] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [tipoChamada, setTipoChamada] = useState('LAEE'); // 'LAEE' ou 'ENGANO'
  const searchRef = useRef(null);

  useEffect(() => {
    document.title = "CRM - CHAMADAS";
    fetchChamada();
    fetchProvincia();
    fetchPatologia();
  }, []);

  const fetchChamada = async () => {
    try {
      setLoading(true); 
      const response = await api.ListChamada();
      
     if (Array.isArray(response)) {
  const dadosFormatados = response
    .sort((a, b) => b.id - a.id) // ORDER BY id DESC
    .map(item => ({
      ...item,
      nome: item.nome,
      sintomas: item.sintomas,
      patologias: item.patologia,
      municipio: item.municipio,
      provincia: item.provincia,
      created_at: item.created_at,
      data: formatarData(item.created_at),
      tipo: item.tipo || 'LAEE'
    }));

  setChamadas(dadosFormatados);
}

    } catch (error) {
      console.error("Erro ao buscar chamadas:", error);
    } finally {
      setLoading(false);  
    }
  };

  const fetchProvincia = async () => {
    try {
      const response = await api.ListProvincia();
      if (Array.isArray(response)) {
        const dadosFormatados = response.map(item => ({
          id: item.id,
          descricao: item.descricao,
        }));
        setProvincias(dadosFormatados);
      }
    } catch (error) {
      console.error("Erro ao buscar dados por agente:", error);
    }
  };

  const fetchPatologia = async () => {
    try {
      const response = await api.ListPatologia();
      if (Array.isArray(response)) {
        const dadosFormatados = response.map(item => ({
          id: item.id,
          descricao: item.descricao,
        }));
        setPatologias(dadosFormatados);
      }
    } catch (error) {
      console.error("Erro ao buscar dados por agente:", error);
    }
  };

  const fetchFaq = async (id_patologia) => {
    try {
      const dados = { id_patologia: id_patologia }
      const response = await api.ListFaq(dados);
      if (Array.isArray(response)) {
        const dadosFormatados = response.map(item => ({
          id: item.id,
          resposta: item.resposta,
          pergunta: item.pergunta,
        }));
        setQuestionarios(dadosFormatados);
      }
    } catch (error) {
      console.error("Erro ao buscar dados por agente:", error);
    }
  };

  const sms_error = (sms) => toast.error(sms, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
  });

  const sms_success = (sms) => toast.success(sms, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
  });

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  };

  const handleQuestionarioChange = async (idQuestionario) => {
    setRespostaQuestionario('');
    form.setFieldsValue({ id_questionario: idQuestionario });

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const questionario = questionarios.find(q => q.id === idQuestionario);

      if (questionario && questionario.resposta) {
        setRespostaQuestionario(questionario.resposta);
      } else {
        setRespostaQuestionario('Sem resposta disponível.');
      }
    } catch (error) {
      setRespostaQuestionario('Erro ao buscar resposta.');
      sms_error('Erro ao buscar resposta: ' + error.message);
    }
  };

  const handlePatologiaChange = (id_patologia) => {
    fetchFaq(id_patologia);
  };

  const handleAdd = async (values) => {
    try {
      setLoading(true);

      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      // Preparar payload baseado no tipo de chamada
      let payload = {
        tipo: tipoChamada,
        id_user: user?.id,
      };

      if (tipoChamada === 'LAEE') {
        // Payload completo para LAEE
        payload = {
          ...payload,
          sintomas: values.sintomas || "",
          nome: values.nome,
          bi: values.bi || "",
          telefone: values.telefone,
          id_patologia: values.id_patologia,
          municipio: values.id_municipio,
          id_provincia: values.id_provincia,
          id_faq: values.id_questionario,
        };
      } else {
        // Payload simplificado para ENGANO
        payload = {
     
          telefone: values.telefone_engano,
          obs: values.observacao || "Chamada registrada como engano",
 
        };
      }

      const response = await api.AddChamada(payload);

      if (response?.success) {
        sms_success(`Chamada ${tipoChamada === 'ENGANO' ? 'de engano' : ''} registrada com sucesso!`);
        form.resetFields();
        setRespostaQuestionario('');
        setIsAddModalVisible(false);
        setTipoChamada('LAEE'); // Resetar para padrão
        fetchChamada();
      } else {
        sms_error("Erro ao registrar chamada.");
      }
    } catch (error) {
      console.error(error);
      sms_error("Erro ao registrar chamada.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => { 
    confirm(); 
    setSearchText(selectedKeys[0]); 
    setSearchedColumn(dataIndex); 
  };

  const handleReset = (clearFilters) => { 
    clearFilters(); 
    setSearchText(''); 
  };

  const handleDelete = async (id) => {
    try {
      setChamadaToDelete(id);
      setIsDeleteModalVisible(true);
    } catch (error) {
      sms_error('Erro ao preparar exclusão: ' + error.message);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchRef}
          placeholder={`Pesquisar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          prefix={<SearchOutlined />}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Pesquisar
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? token.colorPrimary : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: token.colorWarningBg, color: token.colorWarningText }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      filters: [
        { text: 'LAEE', value: 'LAEE' },
        { text: 'Engano', value: 'ENGANO' },
      ],
      onFilter: (value, record) => record.tipo === value,
      render: (tipo) => (
        <Tag 
          color={tipo === 'ENGANO' ? 'orange' : 'blue'} 
          icon={tipo === 'ENGANO' ? <WarningOutlined /> : <FaPhoneAlt />}
        >
          {tipo}
        </Tag>
      ),
    },
    {
      title: 'Utente',
      dataIndex: 'nome',
      key: 'nome',
      width: 180,
      ...getColumnSearchProps('nome'),
      render: (text, record) => (
        <Space>
          <Avatar size="small" style={{ 
            backgroundColor: record.tipo === 'ENGANO' ? token.colorWarning : token.colorPrimary 
          }}>
            {record.tipo === 'ENGANO' ? <WarningOutlined /> : <UserOutlined />}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            {record.telefone && (
              <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                {record.telefone}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Sintomas/Observação',
      dataIndex: 'sintomas',
      key: 'sintomas',
      width: 200,
      ...getColumnSearchProps('sintomas'),
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <span style={{ 
            color: record.tipo === 'ENGANO' ? token.colorWarningText : token.colorText 
          }}>
            {record.tipo === 'ENGANO' ? 'Engano: ' : ''}{text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Patologia',
      dataIndex: 'patologias',
      key: 'patologias',
      width: 150,
      ...getColumnSearchProps('patologias'),
      ellipsis: true,
      render: (text, record) => (
        record.tipo === 'ENGANO' ? 
          <Tag color="default">N/A</Tag> : 
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
      ),
    },
    {
      title: 'Endereço',
      key: 'localizacao',
      width: 180,
      render: (_, record) => (
        record.tipo === 'ENGANO' ? 
          <Tag color="default">Não informado</Tag> :
          <Space direction="vertical" size={0}>
            <div>
              <EnvironmentOutlined style={{ marginRight: 4, color: token.colorSuccess }} />
              {record.municipio}
            </div>
            <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
              {record.provincia}
            </div>
          </Space>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      width: 130,
      sorter: (a, b) => {
        const dateA = new Date(a.data.split(' ')[0].split('/').reverse().join('-'));
        const dateB = new Date(b.data.split(' ')[0].split('/').reverse().join('-'));
        return dateA - dateB;
      },
      ...getColumnSearchProps('data'),
      render: (text, record) => (
        <Tag 
          icon={<CalendarOutlined />} 
          color={record.tipo === 'ENGANO' ? 'default' : 'blue'}
        >
          {text}
        </Tag>
      ),
    },
  ];

  // Resetar formulário quando mudar o tipo de chamada
  const handleTipoChamadaChange = (e) => {
    const novoTipo = e.target.value;
    setTipoChamada(novoTipo);
    form.resetFields();
    setRespostaQuestionario('');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Content style={{ padding: '24px' }}>
        <ToastContainer />
        
        <Card
          title={
            <Space>
              <FaPhoneAlt style={{ color: token.colorPrimary, fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Gestão de Chamadas</Title>
            </Space>
          }
          extra={
            <Space>
              <Button
                type="primary"
                icon={<FaPlus />}
                onClick={() => setIsAddModalVisible(true)}
              >
                Nova Chamada
              </Button>
            </Space>
          }
          style={{
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Total de Chamadas"
                  value={chamadas.length}
                  prefix={<FaPhoneAlt />}
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Chamadas LAEE"
                  value={chamadas.filter(c => c.tipo === 'LAEE').length}
                  prefix={<FaNotesMedical />}
                  valueStyle={{ color: token.colorSuccess }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Chamadas Engano"
                  value={chamadas.filter(c => c.tipo === 'ENGANO').length}
                  prefix={<FaExclamationTriangle />}
                  valueStyle={{ color: token.colorWarning }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Últimas 24h"
                  value={chamadas.filter(c => {
                    const now = new Date();
                    const chamadaDate = new Date(c.data.split(' ')[0].split('/').reverse().join('-'));
                    const diffTime = Math.abs(now - chamadaDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 1;
                  }).length}
                  prefix={<FaCalendarAlt />}
                  valueStyle={{ color: token.colorError }}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Pesquisar em todas as colunas..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ maxWidth: 400 }}
              allowClear
            />
          </div>

          <Table
            columns={columns}
            dataSource={chamadas.filter(chamada => 
              !searchInput || 
              Object.values(chamada).some(value => 
                value?.toString().toLowerCase().includes(searchInput.toLowerCase())
              )
            )}
            loading={loading}
            scroll={{ x: 1300 }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} chamadas`,
              pageSizeOptions: ['10', '20', '50', '100'],
              defaultPageSize: 8,
            }}
          />
        </Card>

        {/* Modal Adicionar Chamada */}
        <Modal
          title={
            <Space>
              <FaPlus style={{ color: token.colorPrimary }} />
              <span>Nova Chamada</span>
            </Space>
          }
          open={isAddModalVisible}
          onCancel={() => {
            form.resetFields();
            setRespostaQuestionario('');
            setTipoChamada('LAEE');
            setIsAddModalVisible(false);
          }}
          footer={null}
          width={tipoChamada === 'ENGANO' ? 500 : 800}
          centered
        >
          <Form form={form} layout="vertical" onFinish={handleAdd}>
            {/* Seletor de Tipo de Chamada */}
            <Form.Item
              label="Tipo de Chamada"
              name="tipo_chamada"
              initialValue="LAEE"
              rules={[{ required: true, message: 'Selecione o tipo de chamada' }]}
            >
              <Radio.Group 
                onChange={handleTipoChamadaChange} 
                value={tipoChamada}
                style={{ width: '100%' }}
              >
                <Radio.Button value="LAEE" style={{ width: '50%', textAlign: 'center' }}>
                  <Space>
                    <MedicineBoxOutlined />
                    LAEE
                  </Space>
                </Radio.Button>
                <Radio.Button value="ENGANO" style={{ width: '50%', textAlign: 'center' }}>
                  <Space>
                    <WarningOutlined />
                    Engano
                  </Space>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {tipoChamada === 'ENGANO' && (
              <Alert
                message="Registro de Chamada Enganosa"
                description="Esta chamada será registrada apenas com número de telefone e observação."
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Formulário para LAEE */}
            {tipoChamada === 'LAEE' ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="nome"
                      label="Nome"
                      rules={[{ required: true, message: 'Nome é obrigatório' }]}
                    >
                      <Input 
                        prefix={<UserOutlined />} 
                        placeholder="Nome do utente"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="telefone"
                      label="Telefone"
                      rules={[{ required: true, message: 'Telefone é obrigatório' }]}
                    >
                      <Input 
                        prefix={<PhoneOutlined />} 
                        placeholder="Número de telefone"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="sintomas"
                  label={
                    <Space>
                      <FaNotesMedical />
                      <span>Sintomas</span>
                    </Space>
                  }
                >
                  <Input.TextArea 
                    rows={3} 
                    placeholder="Descreva os sintomas"
                    showCount 
                    maxLength={500}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="id_patologia"
                      label="Patologia"
                      rules={[{ required: true, message: 'Patologia é obrigatória' }]}
                    >
                      <Select
                        onChange={handlePatologiaChange}
                        suffixIcon={<MedicineBoxOutlined />}
                        size="large"
                        placeholder="Selecione a patologia"
                      >
                        {patologias.map(patologia => (
                          <Option key={patologia.id} value={patologia.id}>
                            {patologia.descricao}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="id_provincia"
                      label="Província"
                      rules={[{ required: true, message: 'Província é obrigatória' }]}
                    >
                      <Select
                        suffixIcon={<EnvironmentOutlined />}
                        size="large"
                        placeholder="Selecione a província"
                      >
                        {provincias.map(provincia => (
                          <Option key={provincia.id} value={provincia.id}>
                            {provincia.descricao}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="id_municipio"
                      label="Município"
                      rules={[{ required: true, message: 'Município é obrigatório' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="Nome do município"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="id_questionario"
                  label="Questionário"
                  rules={[{ required: true, message: 'Selecione um questionário' }]}
                >
                  <Select
                    onChange={handleQuestionarioChange}
                    size="large"
                    placeholder="Selecione um questionário"
                    suffixIcon={<FaQuestionCircle />}
                  >
                    {questionarios.map((q) => (
                      <Option key={q.id} value={q.id}>
                        {q.pergunta}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {respostaQuestionario && (
                  <Card
                    size="small"
                    title="Resposta do Questionário"
                    style={{ marginBottom: 16, borderLeft: `4px solid ${token.colorPrimary}` }}
                  >
                    <Text>{respostaQuestionario}</Text>
                  </Card>
                )}

                <Form.Item name="bi" label="BI (Opcional)">
                  <Input 
                    prefix={<FaIdCard />} 
                    placeholder="Número do bilhete de identidade"
                    size="large"
                  />
                </Form.Item>
              </>
            ) : (
              /* Formulário para ENGANO */
              <>
                <Form.Item
                  name="telefone_engano"
                  label="Número de Telefone"
                  rules={[{ 
                    required: true, 
                    message: 'Número de telefone é obrigatório' 
                  }]}
                >
                  <Input 
                    prefix={<PhoneOutlined />} 
                    placeholder="Número que fez a chamada enganosa"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="observacao"
                  label="Observação"
                >
                  <Input.TextArea 
                    rows={4}
                    placeholder="Observações sobre a chamada enganosa"
                    showCount 
                    maxLength={500}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button 
                  onClick={() => {
                    form.resetFields();
                    setIsAddModalVisible(false);
                    setRespostaQuestionario('');
                    setTipoChamada('LAEE');
                  }}
                  size="large"
                >
                  Cancelar
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  size="large"
                  icon={<FaPlus />}
                  loading={loading}
                >
                  {tipoChamada === 'ENGANO' ? 'Registrar Engano' : 'Criar Chamada'}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de Confirmação de Exclusão */}
        <Modal
          title="Confirmar Exclusão"
          open={isDeleteModalVisible}
          onOk={async () => {
            try {
              // Implementar chamada API para exclusão
              // await api.DeleteChamada(chamadaToDelete);
              sms_success('Chamada excluída com sucesso!');
              fetchChamada();
            } catch (error) {
              sms_error('Erro ao excluir chamada: ' + error.message);
            }
            setIsDeleteModalVisible(false);
            setChamadaToDelete(null);
          }}
          onCancel={() => {
            setIsDeleteModalVisible(false);
            setChamadaToDelete(null);
          }}
          okText="Excluir"
          cancelText="Cancelar"
          okType="danger"
        >
          <p>Tem certeza que deseja excluir esta chamada?</p>
          <p>Esta ação não pode ser desfeita.</p>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Chamada;